import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import GooglePlacesInput from "../maps/GooglePlacesInput";
import GoogleMaps from "../maps/GoogleMaps";
import { useJsApiLoader } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";

const libraries = ["places"];

function Address({
  formData,
  onChange,
  validateFormCallback,
  touched,
  serverErrors = {},
}) {
  const { values } = formData;
  const { latitude: initialLatitude, longitude: initialLongitude } = values;
  // eslint-disable-next-line
  const [typedPlace, setTypedPlace] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(
    initialLatitude && initialLongitude
      ? { lat: initialLatitude, lng: initialLongitude }
      : null
  );
  const [latitude, setLatitude] = useState(initialLatitude || null);
  const [longitude, setLongitude] = useState(initialLongitude || null);
  const [addressDetails, setAddressDetails] = useState(
    values.address_details || ""
  );
  const [currentPlace, setCurrentPlace] = useState(
    values.address_details
      ? { label: values.address_details, value: { description: values.address_details } }
      : null
  );
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
    language: "fr",
    region: "tn",
  });

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressDetails, latitude, longitude]);

  const handleLocationChange = ({ latitude, longitude, address_details }) => {
    setLatitude(latitude);
    setLongitude(longitude);
    setAddressDetails(address_details);
    setSelectedLocation({ lat: latitude, lng: longitude });
    setCurrentPlace({
      label: address_details,
      value: { description: address_details },
    });
  };

  const handlePlaceSelected = (place) => {
    if (place) {
      setSelectedLocation({
        lat: place.latitude,
        lng: place.longitude,
      });
      setLatitude(place.latitude);
      setLongitude(place.longitude);
      setAddressDetails(place.address);
      setCurrentPlace({
        label: place.address,
        value: { description: place.address },
      });
      setErrors({});
    } else {
      setSelectedLocation(null);
      setLatitude(null);
      setLongitude(null);
      setAddressDetails("");
      setCurrentPlace(null);
    }
  };

  const handleInputChange = (inputValue) => {
    setTypedPlace(inputValue);
  };

  const validateForm = () => {
    const newErrors = {};
    // if (!addressDetails && !latitude && !longitude) {
    //   newErrors.address_details = `${t(
    //     "Veuillez sélectionner une adresse sur la carte ou saisir une adresse et sélectionner une suggestion"
    //   )}`;
    // }
    setErrors(newErrors);
    validateFormCallback(Object.keys(newErrors).length === 0);
  };

  useEffect(() => {
    if (touched) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touched]);

  useEffect(() => {
    onChange({
      address_details: addressDetails,
      latitude: latitude,
      longitude: longitude,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressDetails, latitude, longitude]);

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  return (
    <SoftBox>
      <SoftTypography variant="h5" fontWeight="bold">
        {t("Adresse")}
      </SoftTypography>
      <SoftBox mt={1.625}>
        {!isLoaded ? (
          <SoftBox display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} color="inherit" />
            <SoftTypography variant="button" fontWeight="regular" ml={1}>
              {t("Loading...")}
            </SoftTypography>
          </SoftBox>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <GooglePlacesInput
                  onPlaceSelected={handlePlaceSelected}
                  onInputChange={handleInputChange}
                  value={currentPlace}
                />
              </Grid>
            </Grid>
            <GoogleMaps
              onLocationChange={handleLocationChange}
              selectedLocation={selectedLocation}
            />
          </>
        )}
        {touched && errors.address_details && (
          <SoftTypography variant="caption" color="error">
            {errors.address_details}
          </SoftTypography>
        )}
        {touched && errors.coordinates && (
          <SoftTypography variant="caption" color="error">
            {errors.coordinates}
          </SoftTypography>
        )}
      </SoftBox>
    </SoftBox>
  );
}

Address.propTypes = {
  formData: PropTypes.shape({
    formField: PropTypes.shape({
      address_details: PropTypes.object.isRequired,
    }).isRequired,
    values: PropTypes.object.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  validateFormCallback: PropTypes.func.isRequired,
  touched: PropTypes.bool.isRequired,
};

export default Address;
